/**
 * Normalizes JSON panel-data payloads (from embed `panel-data-url` or tenant
 * config) into shapes the React panels can consume. Resolves serializable
 * `iconKey` strings to Lucide components at runtime.
 */
import {
  BookOpen,
  Briefcase,
  FileText,
  GraduationCap,
  PlayCircle,
  Users,
  Waves,
  type LucideIcon,
} from 'lucide-react';
import type { CanvasPanelData } from './CanvasContext';

const ICON_MAP: Record<string, LucideIcon> = {
  BookOpen,
  Briefcase,
  FileText,
  GraduationCap,
  PlayCircle,
  Users,
  Waves,
};

export interface SerializableResource {
  id: string;
  title: string;
  type: 'Video' | 'Guide' | 'Portal' | 'Playbook';
  detail: string;
  description: string;
  tone: 'teal' | 'purple' | 'amber' | 'rose' | 'indigo' | 'emerald';
  iconKey?: string;
  tag?: string;
  url?: string;
  categories?: string[];
}

export interface SerializablePath {
  id: string;
  title: string;
  tagline: string;
  match: number;
  totalTime: string;
  iconKey?: string;
  gradient?: string;
  heroTint?: string;
  milestones: Array<{
    title: string;
    level: string;
    levelLabel: string;
    salary: string;
    timeInRole: string;
    unlocks: string[];
    learningProgram?: string;
  }>;
}

export interface RawPanelDataPayload {
  jobs?: readonly unknown[];
  applications?: readonly unknown[];
  growthPaths?: readonly SerializablePath[];
  resources?: readonly SerializableResource[];
  featuredResource?: SerializableResource;
  agentJobsGuide?: string;
  roleTaxonomy?: readonly unknown[];
}

function resolveIcon(key: string | undefined, fallback: LucideIcon): LucideIcon {
  if (!key) return fallback;
  return ICON_MAP[key] ?? fallback;
}

function normalizeResource(raw: SerializableResource) {
  return {
    ...raw,
    Icon: resolveIcon(raw.iconKey, BookOpen),
  };
}

function normalizePath(raw: SerializablePath) {
  return {
    ...raw,
    Icon: resolveIcon(raw.iconKey, Briefcase),
    gradient: raw.gradient ?? 'linear-gradient(135deg, #006938 0%, #14B8A6 100%)',
    heroTint: raw.heroTint ?? 'from-canvas-primary/10 to-canvas-primary-light/5',
  };
}

/** Parse + hydrate a raw JSON panel-data document for CanvasProvider. */
export function normalizePanelDataPayload(
  payload: RawPanelDataPayload | null | undefined,
): CanvasPanelData {
  if (!payload) return {};

  const resources = payload.resources?.map(normalizeResource);
  const featuredResource = payload.featuredResource
    ? normalizeResource(payload.featuredResource)
    : undefined;
  const growthPaths = payload.growthPaths?.map(normalizePath);

  return {
    jobs: payload.jobs,
    applications: payload.applications,
    resources,
    featuredResource,
    growthPaths,
    agentJobsGuide: payload.agentJobsGuide,
    roleTaxonomy: payload.roleTaxonomy,
  };
}
